import { Globe2, MapPin, Monitor } from 'lucide-react';
import { Link } from 'react-router-dom';

const OurLocations = () => {
  const coverage = [
    { region: 'North America', count: '25+', highlight: 'Major energy markets' },
    { region: 'Europe', count: '40+', highlight: 'Countries covered' },
    { region: 'Middle East', count: '15+', highlight: 'OPEC & regional markets' },
    { region: 'Asia Pacific', count: '30+', highlight: 'Demand centers' },
    { region: 'Latin America', count: '20+', highlight: 'Emerging markets' },
    { region: 'Africa', count: '50+', highlight: 'Resource nations' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F7A4F] via-[#2C6E73] to-[#284A86] text-white py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 whitespace-nowrap">Our Locations</h1>
            <p className="text-base lg:text-lg text-white/90 leading-relaxed">
              Global coverage spanning 200+ countries with digital-first intelligence delivery and strategic market insight.
            </p>
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Globe2 className="w-16 h-16 text-[#007BFF] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Globally Focused Intelligence</h2>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Enerlligence operates as a digital-first intelligence platform with global market coverage. Our analysis spans 200+ countries, providing comprehensive intelligence on energy systems worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coverage.map((area, index) => (
              <div key={index} className="bg-slate-50 p-8 rounded-lg border border-slate-200 hover:border-[#007BFF] transition-all duration-300 text-center">
                <div className="text-4xl font-bold text-[#007BFF] mb-2">{area.count}</div>
                <h3 className="text-xl font-semibold text-[#1F3C88] mb-2">{area.region}</h3>
                <p className="text-base text-slate-600">{area.highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Platform */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-start space-x-3 mb-6">
                <Monitor className="w-10 h-10 text-[#007BFF] flex-shrink-0 mt-1" />
                <h2 className="text-3xl font-bold text-[#1F3C88]">Digital-First Intelligence</h2>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                As a modern intelligence platform, Enerlligence delivers analysis digitally, enabling rapid dissemination of insights, flexible access, and seamless integration with client workflows.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our digital infrastructure supports real-time intelligence delivery, interactive data access, and customized reporting to meet diverse client requirements across time zones and regions.
              </p>
            </div>
            <div className="bg-white p-10 rounded-lg border border-slate-200 space-y-6">
              <div className="flex items-start space-x-4">
                <MapPin className="w-8 h-8 text-[#007BFF] flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-[#1F3C88] mb-2">Market Presence</h3>
                  <p className="text-lg text-slate-600">Intelligence coverage across all major energy markets and emerging regions</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Globe2 className="w-8 h-8 text-[#007BFF] flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-[#1F3C88] mb-2">Global Perspective</h3>
                  <p className="text-lg text-slate-600">Analysis informed by understanding of regional contexts and market linkages</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <Monitor className="w-8 h-8 text-[#007BFF] flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-[#1F3C88] mb-2">Accessible Intelligence</h3>
                  <p className="text-lg text-slate-600">Digital delivery platform ensuring timely access regardless of location</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regional Expertise - 3 Markets in One Row */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#1F3C88] mb-3">Regional Energy Intelligence</h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Our coverage combines global perspective with regional depth across developed, emerging, and frontier markets
            </p>
          </div>

          <div className="grid lg:grid-cols-3 md:grid-cols-1 gap-8">
            {/* Developed Markets */}
            <div className="bg-slate-50 p-8 rounded-lg border border-slate-200 hover:border-[#007BFF] transition-all flex flex-col h-full">
              <h3 className="text-2xl font-bold text-[#1F3C88] mb-4">Developed Markets</h3>
              <p className="text-base text-slate-600 leading-relaxed mb-6 flex-grow">
                Comprehensive intelligence on mature energy markets including North America, Europe, and developed Asia Pacific, covering policy evolution, market design, and transition dynamics.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Regulatory frameworks</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Market liberalization trends</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Energy transition pathways</span>
                </li>
              </ul>
            </div>

            {/* Emerging Markets */}
            <div className="bg-slate-50 p-8 rounded-lg border border-slate-200 hover:border-[#007BFF] transition-all flex flex-col h-full">
              <h3 className="text-2xl font-bold text-[#1F3C88] mb-4">Emerging Markets</h3>
              <p className="text-base text-slate-600 leading-relaxed mb-6 flex-grow">
                Strategic analysis of growth markets across Latin America, Africa, Middle East, and developing Asia, focusing on demand growth, infrastructure development, and investment opportunities.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Demand growth drivers</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Infrastructure investment</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Market entry strategies</span>
                </li>
              </ul>
            </div>

            {/* Frontier Markets */}
            <div className="bg-slate-50 p-8 rounded-lg border border-slate-200 hover:border-[#007BFF] transition-all flex flex-col h-full">
              <h3 className="text-2xl font-bold text-[#1F3C88] mb-4">Frontier Markets</h3>
              <p className="text-base text-slate-600 leading-relaxed mb-6 flex-grow">
                Specialized intelligence on early-stage energy markets with significant resource potential, covering policy frameworks, licensing activity, infrastructure needs, and strategic partnerships.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Resource development potential</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Policy and licensing frameworks</span>
                </li>
                <li className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-[#007BFF] rounded-full mt-1.5 flex-shrink-0"></div>
                  <span className="text-sm text-slate-600">Strategic partnership opportunities</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Globe2 className="w-16 h-16 text-[#007BFF] mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-[#1F3C88] mb-6">Connect With Us</h2>
          <p className="text-xl text-slate-700 leading-relaxed mb-8">
            Wherever your organization operates, Enerlligence provides the intelligence you need
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-[#007BFF] text-white font-semibold rounded-md hover:bg-[#0066DD] transition-all duration-200 shadow-lg"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
};

export default OurLocations;
